/**
 * Copyright 2021 Felix Martinez
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"),to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
 * ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import {
  Camera,
  ClampToEdgeWrapping,
  Clock,
  DataTexture,
  FloatType,
  NearestFilter,
  Object3D,
  RawShaderMaterial,
  RGBAFormat,
  TextureLoader,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three'

import updatePositionsVertex from '../../shaders/raw/gpu-simulation-grid/vs-base.glsl'
import updatePositionsFragment from '../../shaders/raw/gpu-simulation-grid/fs-update-positions.glsl'

import { getTextureDimensionsPot } from '@siroko/math'
import PingPongRenderTarget from './PingPongRenderTarget'

class GPUSimulation extends Object3D {
  public textureDimensions?: Array<number>
  public currentPositionRendertarget?: WebGLRenderTarget

  private positionsDataTexture?: DataTexture
  private positionsPingpong?: PingPongRenderTarget
  private updatePositionsMaterial?: RawShaderMaterial

  public alignFactor: number = 1.766
  public cohesionFactor: number = 1.733
  public separationFactor: number = 1.98
  public forceToCenterFactor: number = 3.8
  public rangeAlign: number = 6.38
  public rangeCohesion: number = 6.38
  public range: number = 6.121
  public maxSpeed: number = 26.766
  public maxForce: number = 0.938
  private textureLoader: TextureLoader

  constructor(
    public particleCount: number,
    private renderer: WebGLRenderer,
    private cameraTarget: Vector3,
    private clock: Clock
  ) {
    super()
    this.setup()
    this.setupMaterials()
    this.updateRenderTargets()
  }

  public update(dt: number) {
    this.updateRenderTargets()

    this.updatePositionsMaterial!.uniforms.uPositionsTexture.value = this.currentPositionRendertarget!.texture
    this.updatePositionsMaterial!.uniforms.uPrevPositionsTexture.value = this.positionsPingpong?.getPreviousRT()?.texture
    this.updatePositionsMaterial!.uniforms.uDeltaTime.value = dt
    this.updatePositionsMaterial!.uniforms.uTime.value = this.clock.getElapsedTime()
    console.log(this.cameraTarget)
    this.updatePositionsMaterial!.uniforms.cameraPosition.value = this.cameraTarget
  }

  private updateRenderTargets() {
    this.currentPositionRendertarget = this.positionsPingpong?.pass(
      this.updatePositionsMaterial!
    )
  }

  private setup(): void {
    this.textureLoader = new TextureLoader()
    this.textureDimensions = getTextureDimensionsPot(this.particleCount)
    const totalPot: number =
      this.textureDimensions![0] * this.textureDimensions![1]
    const positions: Float32Array = new Float32Array(totalPot * 4)
    const dimensionX = 800
    const dimensionZ = 80
    const extension = 700
    for (let i = 0; i < this.particleCount; i++) {
      const x: number = ((i % dimensionX / dimensionX) - 0.5) * 2
      const y: number = 0 //((Math.floor(i / (dimension * dimension)) / dimension) - 0.5) * 2
      const z: number = (((Math.floor(i / dimensionX) % dimensionZ) / dimensionZ) - 0.5) * 2

      positions[i * 4] = x * extension
      positions[i * 4 + 1] = y * extension
      positions[i * 4 + 2] = z * extension * (dimensionZ / dimensionX)
      positions[i * 4 + 3] = (window as any).fxrand()
    }

    this.positionsDataTexture = new DataTexture(
      positions,
      this.textureDimensions![0],
      this.textureDimensions![1],
      RGBAFormat,
      FloatType,
      undefined,
      ClampToEdgeWrapping,
      ClampToEdgeWrapping,
      NearestFilter,
      NearestFilter
    )

    this.positionsPingpong = new PingPongRenderTarget(
      this.textureDimensions!,
      this.renderer
    )
  }

  private setupMaterials() {
    this.updatePositionsMaterial = new RawShaderMaterial({
      vertexShader: updatePositionsVertex,
      fragmentShader: updatePositionsFragment,
      uniforms: {
        uPositionsTexture: { value: this.positionsDataTexture },
        uPrevPositionsTexture: { value: this.positionsDataTexture },
        uHeightMap: { value: this.textureLoader.load('./assets/height.png') },
        uTime: { value: 0 },
        uLacunarity: {value: (window as any).fxrand() * 0.01},
        uOffsetNoise: {value: (window as any).fxrand() * 100.0},
        uDeltaTime: { value: this.clock.getDelta() },
        cameraPosition: { value: this.cameraTarget },
      },
    })
  }
}

export default GPUSimulation
